/**
 * Импорт аккаунтов из файла (CSV/TSV/текстовик с разделителями) или вставки.
 * Формат не фиксирован: разделитель определяется автоматически (можно
 * переопределить), каждая колонка вручную мапится на поле аккаунта —
 * так ляжет любой формат, который отдаст софт.
 */
import { useMemo, useState } from 'react';
import type { BulkImportResult, ModelSummary, PhoneSummary } from '@wml-insta/shared';
import { ACCOUNT_STATUSES } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { ModalPortal } from './ModalPortal.js';

interface Props {
  phones: PhoneSummary[];
  models: ModelSummary[];
  defaultPhoneId?: string;
  defaultModelId?: string;
  onClose: () => void;
  onDone: (result: BulkImportResult) => void;
}

const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '— пропустить —' },
  { value: 'username', label: 'Юзернейм *' },
  { value: 'password', label: 'Пароль' },
  { value: 'totpSecret', label: '2ФА ключ' },
  { value: 'email', label: 'Почта' },
  { value: 'emailPassword', label: 'Пароль почты' },
  { value: 'containerId', label: 'Container ID *' },
  { value: 'country', label: 'Страна *' },
  { value: 'language', label: 'Язык *' },
  { value: 'phoneName', label: 'Телефон (имя)' },
  { value: 'phoneSerial', label: 'Серийник телефона' },
  { value: 'modelName', label: 'Модель (имя)' },
  { value: 'status', label: 'Статус' },
  { value: 'accountType', label: 'Тип аккаунта' },
  { value: 'batch', label: 'Пачка' },
  { value: 'cloneVersion', label: 'Версия клона' },
  { value: 'creoName', label: 'Крео' },
  { value: 'link', label: 'Линк' },
  { value: 'startDate', label: 'Дата старта' },
  { value: 'viewsCategory', label: 'Категория просмотров' },
  { value: 'views24h', label: 'Просмотры 24ч' },
  { value: 'videos24h', label: 'Видео 24ч' },
  { value: 'viewsTotal', label: 'Просмотры всего' },
  { value: 'note', label: 'Заметка' },
];

const NUM_FIELDS = new Set(['containerId', 'views24h', 'videos24h', 'viewsTotal']);

const AUTO_MAP: [RegExp, string][] = [
  [/^(username|юзернейм|login|логин|user)$/i, 'username'],
  [/^(password|пароль)$/i, 'password'],
  [/^(totp|totp_secret|2фа|2fa|2фа ключ|secret)$/i, 'totpSecret'],
  [/^(email|почта|mail)$/i, 'email'],
  [/^(email_password|пароль от почты|пароль почты)$/i, 'emailPassword'],
  [/^(container|container_id|container id|контейнер|айди контейнера)$/i, 'containerId'],
  [/^(country|страна|гео|geo)$/i, 'country'],
  [/^(language|lang|язык|язык контейнера)$/i, 'language'],
  [/^(phone|телефон|device)$/i, 'phoneName'],
  [/^(серийник( телефона)?|серийный номер|serial|sn|device_serial)$/i, 'phoneSerial'],
  [/^(model|модель|имя модели|name)$/i, 'modelName'],
  [/^(status|статус)$/i, 'status'],
  [/^(type|тип|тип аккаунта|account type)$/i, 'accountType'],
  [/^(batch|пачка)$/i, 'batch'],
  [/^(clone|версия клона|clone_version)$/i, 'cloneVersion'],
  [/^(creo|крео|название крео)$/i, 'creoName'],
  [/^(link|линк)$/i, 'link'],
  [/^(start|дата старта|start_date)$/i, 'startDate'],
  [/^(категория просмотров|views category)$/i, 'viewsCategory'],
  [/^(сумма просмотров за 24ч|views_24h|views 24h)$/i, 'views24h'],
  [/^(кол-во видео за 24ч|videos_24h)$/i, 'videos24h'],
  [/^(сумма просмотров|views|total views)$/i, 'viewsTotal'],
  [/^(note|заметка|причина)$/i, 'note'],
];

const DELIMS: { value: string; label: string }[] = [
  { value: 'auto', label: 'Авто' },
  { value: '\t', label: 'Таб' },
  { value: ';', label: '«;»' },
  { value: ',', label: '«,»' },
  { value: '|', label: '«|»' },
  { value: ':', label: '«:»' },
];

function detectDelim(line: string): string {
  let best = '\t';
  let bestCount = 0;
  for (const d of ['\t', ';', ',', '|', ':']) {
    const count = line.split(d).length - 1;
    if (count > bestCount) { best = d; bestCount = count; }
  }
  return best;
}

/** Разбор строки с учётом кавычек ("a;b" не режется по «;»). */
function splitLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"' && cur === '') {
      inQuotes = true;
    } else if (ch === delim) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/** Пример CSV: канонические заголовки (порядок колонок неважен — маппинг по ним). */
const SAMPLE_CSV = [
  ['container ID', 'Юзернейм', 'Пароль', '2ФА ключ', 'Страна', 'Язык', 'Почта', 'Пароль почты', 'Телефон', 'Серийник телефона', 'Модель', 'Статус', 'Тип аккаунта', 'Пачка', 'Крео', 'Линк', 'Заметка'],
  ['12', 'examplename1', 'p@ssw0rd', 'LBKF4QACXXXXXXXX', 'PL', 'pl', 'mail1@example.com', 'mailpass', 'Samsung1.1', '', 'Royce', '', 'Самореги Прокси', 'Пачка 5', 'creo_v2', 'allmyspicylinks.com/examplename1', 'статус пустой — акк уйдёт под создание контейнера'],
  ['7', 'examplename2', 'p@ssw0rd2', 'ABCDEFGHXXXXXXXX', 'US', 'en', '', '', '', 'R58M42ABCDE', 'Lolly', '', '', '', '', '', 'через серийник телефона'],
].map((r) => r.join(';')).join('\n');

function downloadSample() {
  const blob = new Blob(['\ufeff' + SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'wml-import-example.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function parseDate(v: string): string | null {
  const t = v.trim();
  if (t === '') return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (iso) return iso[0]!;
  const ru = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})/.exec(t);
  if (ru) {
    const y = ru[3]!.length === 2 ? `20${ru[3]}` : ru[3]!;
    return `${y}-${ru[2]!.padStart(2, '0')}-${ru[1]!.padStart(2, '0')}`;
  }
  return null;
}

export function ImportAccountsModal({ phones, models, defaultPhoneId, defaultModelId, onClose, onDone }: Props) {
  const [raw, setRaw] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [delimChoice, setDelimChoice] = useState('auto');
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [autoMapped, setAutoMapped] = useState(false);
  const [fixedPhoneId, setFixedPhoneId] = useState(defaultPhoneId ?? '');
  const [fixedModelId, setFixedModelId] = useState(defaultModelId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const lines = useMemo(
    () => raw.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== ''),
    [raw],
  );
  const delim = delimChoice === 'auto' ? (lines[0] ? detectDelim(lines[0]) : '\t') : delimChoice;
  const rows = useMemo(() => lines.map((l) => splitLine(l, delim)), [lines, delim]);
  const header = hasHeader ? rows[0] ?? [] : [];
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const colCount = rows.reduce((m, r) => Math.max(m, r.length), 0);

  // Авто-маппинг по заголовкам — один раз после загрузки данных.
  if (!autoMapped && hasHeader && header.length > 0) {
    const next: Record<number, string> = {};
    header.forEach((h, i) => {
      const hit = AUTO_MAP.find(([re]) => re.test(h.trim()));
      if (hit) next[i] = hit[1];
    });
    setMapping(next);
    setAutoMapped(true);
  }

  const usernameMapped = Object.values(mapping).includes('username');
  const phoneMapped = Object.values(mapping).includes('phoneName');
  const serialMapped = Object.values(mapping).includes('phoneSerial');
  const containerMapped = Object.values(mapping).includes('containerId');
  const countryMapped = Object.values(mapping).includes('country');
  const languageMapped = Object.values(mapping).includes('language');
  const canImport = dataRows.length > 0 && usernameMapped && containerMapped && countryMapped && languageMapped
    && (phoneMapped || serialMapped || fixedPhoneId);

  function loadFile(f: File) {
    setFileName(f.name);
    setAutoMapped(false);
    setMapping({});
    f.text().then(setRaw).catch(() => setErr('Не удалось прочитать файл'));
  }

  async function runImport() {
    if (!canImport || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const items = dataRows.map((cells) => {
        const item: Record<string, unknown> = {};
        for (const [idxStr, field] of Object.entries(mapping)) {
          if (!field) continue;
          const v = (cells[Number(idxStr)] ?? '').trim();
          if (v === '') continue;
          if (NUM_FIELDS.has(field)) {
            const n = Number(v.replace(/\s+/g, '').replace(',', '.'));
            if (Number.isFinite(n) && n >= 0) item[field] = Math.round(n);
          } else if (field === 'startDate') {
            const d = parseDate(v);
            if (d) item.startDate = d;
          } else if (field === 'status') {
            const s = ACCOUNT_STATUSES.find((x) => x.toLowerCase() === v.toLowerCase());
            if (s) item.status = s;
          } else {
            item[field] = v;
          }
        }
        if (!item.phoneName && !item.phoneSerial && fixedPhoneId) item.phoneId = fixedPhoneId;
        if (!item.modelName && fixedModelId) item.modelId = fixedModelId;
        return item;
      });
      const res = await api<BulkImportResult>('/api/accounts/bulk', {
        method: 'POST',
        body: JSON.stringify({ accounts: items }),
      });
      setResult(res);
      onDone(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Импорт не удался');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalPortal>
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal-panel" style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Импорт аккаунтов из файла</div>

        {result ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 15 }}>
              Создано: <b style={{ color: '#4ade80' }}>{result.created.length}</b>
              {' · '}Отклонено: <b style={{ color: result.rejected.length ? 'var(--accent)' : 'inherit' }}>{result.rejected.length}</b>
            </div>
            {result.rejected.length > 0 && (
              <div style={{ maxHeight: 260, overflowY: 'auto', fontSize: 12.5, background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
                {result.rejected.map((r) => (
                  <div key={r.index} style={{ padding: '3px 0', color: 'var(--text-muted)' }}>
                    строка {r.index + 1}{r.username ? ` (@${r.username})` : ''}: {r.error}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" style={{ width: 'auto', padding: '0 20px' }} onClick={onClose}>Готово</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="btn-secondary" style={{ cursor: 'pointer' }}>
                {fileName ?? 'Выбрать файл (.csv / .txt)'}
                <input
                  type="file"
                  accept=".csv,.txt,.tsv"
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
                />
              </label>
              <button type="button" className="link-btn" onClick={downloadSample}>Скачать пример .csv</button>
              <span style={{ color: 'var(--text-subtle)', fontSize: 12.5 }}>или вставь текст ниже</span>
              <div style={{ flex: 1 }} />
              <select className="admin-input" value={delimChoice} onChange={(e) => { setDelimChoice(e.target.value); setAutoMapped(false); }} title="Разделитель" style={{ width: 110 }}>
                {DELIMS.map((d) => <option key={d.label} value={d.value}>{d.label}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={hasHeader} onChange={(e) => { setHasHeader(e.target.checked); setAutoMapped(false); setMapping({}); }} style={{ accentColor: 'var(--accent)' }} />
                1-я строка — заголовки
              </label>
            </div>

            <textarea
              className="admin-input"
              rows={raw ? 4 : 7}
              placeholder='container ID:username:password:2fa:страна:язык:модель (необязательно):серийник телефона (необязательно)'
              value={raw}
              onChange={(e) => { setRaw(e.target.value); setAutoMapped(false); }}
              style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, width: '100%', resize: 'vertical' }}
            />

            {dataRows.length > 0 && (
              <>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {dataRows.length} строк · {colCount} колонок · укажи, что в какой колонке:
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <table className="admin-table" style={{ minWidth: colCount * 160 }}>
                    <thead>
                      <tr>
                        {Array.from({ length: colCount }, (_, i) => (
                          <th key={i} style={{ padding: '6px 8px' }}>
                            <select
                              className="admin-input"
                              value={mapping[i] ?? ''}
                              onChange={(e) => setMapping((m) => ({ ...m, [i]: e.target.value }))}
                              style={{ width: '100%', fontSize: 12 }}
                            >
                              {FIELD_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                            {hasHeader && <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-subtle)', textTransform: 'none', letterSpacing: 0 }}>{header[i] ?? ''}</div>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dataRows.slice(0, 5).map((r, ri) => (
                        <tr key={ri}>
                          {Array.from({ length: colCount }, (_, i) => (
                            <td key={i} style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[i] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {!phoneMapped && !serialMapped && (
                    <label className="field" style={{ margin: 0, minWidth: 200 }}>
                      <span>Телефон для всех строк *</span>
                      <select className="admin-input" value={fixedPhoneId} onChange={(e) => setFixedPhoneId(e.target.value)}>
                        <option value="">— выбери —</option>
                        {phones.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </label>
                  )}
                  {!Object.values(mapping).includes('modelName') && (
                    <label className="field" style={{ margin: 0, minWidth: 200 }}>
                      <span>Модель для всех строк</span>
                      <select className="admin-input" value={fixedModelId} onChange={(e) => setFixedModelId(e.target.value)}>
                        <option value="">— без модели —</option>
                        {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </label>
                  )}
                </div>
                {!usernameMapped && (
                  <div style={{ fontSize: 12.5, color: 'var(--accent)' }}>Назначь колонку «Юзернейм» — без неё импорт не пойдёт.</div>
                )}
                {!containerMapped && (
                  <div style={{ fontSize: 12.5, color: 'var(--accent)' }}>Назначь колонку «Container ID» — она обязательна при импорте.</div>
                )}
                {!countryMapped && (
                  <div style={{ fontSize: 12.5, color: 'var(--accent)' }}>Назначь колонку «Страна» (код как в разделе «Координаты»: NL, UK, FR, PL, US) — обязательна.</div>
                )}
                {!languageMapped && (
                  <div style={{ fontSize: 12.5, color: 'var(--accent)' }}>Назначь колонку «Язык» (язык контейнера) — обязательна.</div>
                )}
              </>
            )}

            {err && <div className="error">{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={onClose} disabled={busy}>Отмена</button>
              <button className="btn-primary" style={{ width: 'auto', padding: '0 20px' }} disabled={!canImport || busy} onClick={runImport}>
                {busy ? 'Импортирую…' : `Импортировать ${dataRows.length} строк`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </ModalPortal>
  );
}
